import React, { useMemo, useState, useEffect } from 'react';
import { Box, Popover, Button, Grid, Typography, Divider } from '@mui/material';
import { Translate } from '@mui/icons-material';
import useInteractiveText from '../../hooks/useInteractiveText';
import WordCard from '../Learning/WordCard';
import RateLimitDialog from '../Common/RateLimitDialog';
import PropTypes from 'prop-types';

const VideoCaptionInteractiveTextViewer = ({ 
    formattedText, 
    textId, 
    startCount = 0,
    wholeTextTranslation = null,
    baseClassName = 'video-caption-interactive-text',
    style = {},
    showInteractiveStyles = true,
    source = null,
    onWordDisplayed = null,
    enableTranslation = true,
    forceShowTranslation = null
}) => {
    // Translation state - can be controlled individually or externally
    const [showTranslation, setShowTranslation] = useState(false);
    const [isIndividuallyControlled, setIsIndividuallyControlled] = useState(false);

    // Sync with external forceShowTranslation prop
    // When global control changes, reset individual control and apply the new value
    useEffect(() => {
        if (forceShowTranslation !== null) {
            setIsIndividuallyControlled(false);
            setShowTranslation(forceShowTranslation);
        }
    }, [forceShowTranslation]);

    // Determine effective translation state
    const effectiveShowTranslation = forceShowTranslation !== null && !isIndividuallyControlled 
        ? forceShowTranslation 
        : showTranslation;

    // Generate unique className for this instance
    const uniqueClassName = useMemo(() => {
        return `${baseClassName}-${textId.replace(/[^a-zA-Z0-9]/g, '')}-${Math.random().toString(36).substr(2, 9)}`;
    }, [baseClassName, textId]);

    const {
        clickedWord,
        anchorEl,
        open,
        handleClose,
        rateLimitError,
        setRateLimitError
    } = useInteractiveText(formattedText, textId, uniqueClassName, startCount, effectiveShowTranslation ? 1 : 0);

    const defaultStyle = {
        lineHeight: 1.8,
        fontSize: '1.1rem',
        fontFamily: 'Georgia, serif',
        textAlign: 'center',
        ...style
    };

    // Handle translation toggle - mark as individually controlled
    const handleToggleTranslation = () => {
        setIsIndividuallyControlled(true);
        setShowTranslation(!showTranslation);
    };

    return (
        <Box>
            {/* Interactive Styles */}
            {showInteractiveStyles && (
                <style>
                    {`
                        .${uniqueClassName} .chp {
                            display: inline;
                            padding: 2px 1px;
                            border-radius: 2px;
                            transition: background-color 0.2s;
                            cursor: pointer;
                        }
                        .${uniqueClassName} .chp:hover {
                            background-color: rgba(25, 118, 210, 0.1);
                        }
                    `}
                </style>
            )}

            {/* Translation Toggle Button - Always show if translation is available */}
            {enableTranslation && wholeTextTranslation && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button
                        variant="text"
                        size="small"
                        startIcon={<Translate />}
                        onClick={handleToggleTranslation}
                        sx={{ 
                            fontSize: '0.875rem',
                            textTransform: 'none',
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    >
                        {effectiveShowTranslation ? 'Hide Translation' : 'Show Translation'}
                    </Button>
                </Box>
            )}

            {/* Content Layout */}
            {!effectiveShowTranslation ? (
                /* Single Column - Original Content */
                <div 
                    className={uniqueClassName}
                    style={defaultStyle}
                    dangerouslySetInnerHTML={{ __html: formattedText }}
                />
            ) : (
                /* Responsive Layout - Stacked on mobile, Side by Side on desktop */
                <Grid container spacing={2}>
                    {/* Original Text Column */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, minHeight: '100%' }}>
                            <Typography 
                                variant="h6" 
                                gutterBottom 
                                sx={{ 
                                    fontWeight: 'bold', 
                                    color: 'primary.main',
                                    textAlign: 'center',
                                    fontSize: '1rem',
                                    mb: 1
                                }}
                            >
                                Original
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            
                            <div 
                                className={uniqueClassName}
                                style={{
                                    ...defaultStyle,
                                    fontSize: '0.95rem'
                                }}
                                dangerouslySetInnerHTML={{ __html: formattedText }}
                            />
                        </Box>
                    </Grid>
                    
                    {/* Translation Column */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 1, p: 2, minHeight: '100%' }}>
                            <Typography 
                                variant="h6" 
                                gutterBottom 
                                sx={{ 
                                    fontWeight: 'bold', 
                                    color: 'secondary.main',
                                    fontSize: '1rem',
                                    textAlign: 'center',
                                    mb: 1
                                }}
                            >
                                Translation
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            
                            {wholeTextTranslation ? (
                                <Box
                                    sx={{
                                        lineHeight: 1.8,
                                        fontSize: '0.95rem',
                                        fontFamily: 'Georgia, serif',
                                        textAlign: 'center',
                                        '& p': { 
                                            fontSize: '0.95rem !important',
                                            fontFamily: 'Georgia, serif',
                                            lineHeight: 1.8,
                                            margin: '0 0 16px 0'
                                        },
                                        '& *': { 
                                            fontFamily: 'Georgia, serif',
                                            fontSize: '0.95rem !important',
                                            lineHeight: 1.8
                                        }
                                    }}
                                >
                                    {wholeTextTranslation}
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography 
                                        variant="body1" 
                                        color="text.secondary"
                                        sx={{ fontSize: '0.95rem' }}
                                    >
                                        Translation not available
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            )}

            {/* Word Details Popover */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                disableRestoreFocus={true}
                disableEnforceFocus={true}
                disableAutoFocus={true}
                BackdropProps={{
                    invisible: true
                }}
                PaperProps={{
                    elevation: 8,
                    sx: {
                        maxWidth: { xs: 280, sm: 300, md: 400 },
                        width: { xs: '90vw', sm: 'auto' },
                        border: '1px solid #e0e0e0',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }
                }}
                container={typeof document !== 'undefined' ? document.body : undefined}
                marginThreshold={16}
            >
                {clickedWord && <WordCard word={clickedWord} onClose={handleClose} source={source} onWordDisplayed={onWordDisplayed} />}
            </Popover>

            {/* Rate Limit Dialog */}
            <RateLimitDialog 
                open={!!rateLimitError}
                onClose={() => setRateLimitError(null)}
                errorData={rateLimitError}
            />
        </Box>
    );
};

VideoCaptionInteractiveTextViewer.propTypes = {
    formattedText: PropTypes.string.isRequired,
    textId: PropTypes.string.isRequired,
    startCount: PropTypes.number,
    wholeTextTranslation: PropTypes.string,
    baseClassName: PropTypes.string,
    style: PropTypes.object,
    showInteractiveStyles: PropTypes.bool,
    source: PropTypes.object,
    onWordDisplayed: PropTypes.func,
    enableTranslation: PropTypes.bool
};

export default VideoCaptionInteractiveTextViewer; 